import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"

Chart.register(...registerables)

export default class extends Controller {
  static targets = ["inputTokensChart", "outputTokensChart", "costChart", "serviceCostChart"]
  static values = { chartsDataUrl: String }

  connect() {
    this.currentPeriod = 'weekly'
    this.inputChart = null
    this.outputChart = null
    this.costChart = null
    this.serviceCostChart = null
    this.loadChartData()
  }

  async loadChartData() {
    try {
      // Build URL from the base URL value
      const baseUrl = this.chartsDataUrlValue
      const url = new URL(baseUrl, window.location.origin)
      url.searchParams.set('period', this.currentPeriod)
      
      console.log('Loading chart data from:', url.toString())
      const response = await fetch(url.toString())
      if (!response.ok) {
        const errorText = await response.text()
        console.error('Response error:', response.status, errorText)
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const data = await response.json()
      console.log('Chart data received:', data)
      
      if (!data || !data.datasets) {
        console.warn('No datasets in response, rendering empty charts')
        this.renderCharts({ labels: [], datasets: {}, service_costs: {}, period: this.currentPeriod })
        return
      }
      
      this.renderCharts(data)
    } catch (error) {
      console.error('Error loading chart data:', error)
      this.renderCharts({ labels: [], datasets: {}, service_costs: {}, period: this.currentPeriod })
    }
  }

  switchPeriod(event) {
    const period = event.currentTarget.dataset.period
    if (period === this.currentPeriod) return
    
    this.currentPeriod = period
    
    // Update button states
    event.currentTarget.parentElement.querySelectorAll('button').forEach(btn => {
      btn.classList.remove('active')
    })
    event.currentTarget.classList.add('active')
    
    // Reload charts
    this.loadChartData()
  }

  renderCharts(data) {
    if (!data || !data.datasets || !data.labels) {
      console.error('Invalid chart data:', data)
      return
    }
    
    if (this.inputChart) {
      this.inputChart.destroy()
      this.inputChart = null
    }
    if (this.outputChart) {
      this.outputChart.destroy()
      this.outputChart = null
    }
    if (this.costChart) {
      this.costChart.destroy()
      this.costChart = null
    }
    if (this.serviceCostChart) {
      this.serviceCostChart.destroy()
      this.serviceCostChart = null
    }
    
    // Model cost chart (stacked bar, model breakdown only)
    if (this.hasCostChartTarget) {
      const costDatasets = this.prepareDatasets(data.datasets, 'cost')
      if (costDatasets.length > 0) {
        this.costChart = this.createCostChart(this.costChartTarget, costDatasets, data.labels)
      }
    }

    // Service cost chart (stacked bar: chat / workflow / dictation)
    if (this.hasServiceCostChartTarget) {
      const serviceCostDatasets = this.prepareServiceCostDatasets(data.service_costs)
      this.serviceCostChart = this.createCostChart(this.serviceCostChartTarget, serviceCostDatasets, data.labels)
    }
    
    // Token charts (model breakdown)
    const inputDatasets = this.prepareDatasets(data.datasets, 'input_tokens')
    this.inputChart = this.createChart(this.inputTokensChartTarget, inputDatasets, data.labels, 'input_tokens')
    
    const outputDatasets = this.prepareDatasets(data.datasets, 'output_tokens')
    this.outputChart = this.createChart(this.outputTokensChartTarget, outputDatasets, data.labels, 'output_tokens')
  }

  prepareServiceCostDatasets(serviceCosts) {
    if (!serviceCosts) return []
    const palette = [
      { label: 'AI Chat',       bg: 'rgba(54, 162, 235, 0.7)',  border: 'rgba(54, 162, 235, 1)',  key: 'chat' },
      { label: 'Beacon',        bg: 'rgba(23, 162, 184, 0.7)',  border: 'rgba(23, 162, 184, 1)',  key: 'beacon' },
      { label: 'Messenger',     bg: 'rgba(108, 117, 125, 0.7)', border: 'rgba(108, 117, 125, 1)', key: 'messenger' },
      { label: 'Hangasszisztens', bg: 'rgba(255, 193, 7, 0.7)', border: 'rgba(255, 193, 7, 1)',   key: 'voice' },
      { label: 'Telefonközpont', bg: 'rgba(33, 37, 41, 0.7)', border: 'rgba(33, 37, 41, 1)', key: 'call_center' },
      { label: 'AI Workflow',   bg: 'rgba(255, 159, 64, 0.7)',  border: 'rgba(255, 159, 64, 1)',  key: 'workflow' },
      { label: 'AI Diktálás',   bg: 'rgba(153, 102, 255, 0.7)', border: 'rgba(153, 102, 255, 1)', key: 'dictation' }
    ]
    return palette
      .filter(p => serviceCosts[p.key] && serviceCosts[p.key].some(v => v > 0))
      .map(p => ({
        label: p.label,
        data: serviceCosts[p.key],
        backgroundColor: p.bg,
        borderColor: p.border,
        borderWidth: 2
      }))
  }

  prepareDatasets(dataByModel, tokenType) {
    const datasets = []
    const colors = [
      { bg: 'rgba(54, 162, 235, 0.6)', border: 'rgba(54, 162, 235, 1)' },
      { bg: 'rgba(255, 99, 132, 0.6)', border: 'rgba(255, 99, 132, 1)' },
      { bg: 'rgba(75, 192, 192, 0.6)', border: 'rgba(75, 192, 192, 1)' },
      { bg: 'rgba(153, 102, 255, 0.6)', border: 'rgba(153, 102, 255, 1)' },
      { bg: 'rgba(201, 203, 207, 0.6)', border: 'rgba(201, 203, 207, 1)' }
    ]
    
    let colorIndex = 0
    Object.keys(dataByModel).forEach(modelId => {
      const modelData = dataByModel[modelId]
      // Backend already sends friendly display names (e.g. "Anthropic Sonnet")
      // instead of raw Bedrock model IDs — use the key as-is.
      const displayName = modelId

      datasets.push({
        label: displayName,
        data: modelData[tokenType],
        backgroundColor: colors[colorIndex % colors.length].bg,
        borderColor: colors[colorIndex % colors.length].border,
        borderWidth: 2,
        tension: 0.4
      })
      colorIndex++
    })
    
    return datasets
  }

  createChart(canvas, datasets, labels, tokenType) {
    const ctx = canvas.getContext('2d')
    
    // Format labels for display
    const formattedLabels = labels.map(label => {
      if (this.currentPeriod === 'monthly') {
        // Format: YYYY-MM -> 2024-01 -> Jan 2024
        const [year, month] = label.split('-')
        const date = new Date(year, month - 1)
        return date.toLocaleDateString('hu-HU', { month: 'short', year: 'numeric' })
      } else {
        // Format: YYYY-MM-DD -> 2024-01-15 -> 2024. jan 15.
        const date = new Date(label)
        return date.toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' })
      }
    })
    
    return new Chart(ctx, {
      type: 'line',
      data: {
        labels: formattedLabels,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function(context) {
                const label = context.dataset.label || ''
                const value = context.parsed.y || 0
                return `${label}: ${value.toLocaleString('hu-HU')}`
              }
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: this.currentPeriod === 'monthly' ? 'Hónap' : 'Hét'
            },
            ticks: {
              maxRotation: 45,
              minRotation: 45
            }
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Tokenek száma'
            },
            ticks: {
              callback: function(value) {
                return value.toLocaleString('hu-HU')
              }
            }
          }
        }
      }
    })
  }

  createCostChart(canvas, datasets, labels) {
    const ctx = canvas.getContext('2d')
    
    // Format labels for display
    const formattedLabels = labels.map(label => {
      if (this.currentPeriod === 'monthly') {
        const [year, month] = label.split('-')
        const date = new Date(year, month - 1)
        return date.toLocaleDateString('hu-HU', { month: 'short', year: 'numeric' })
      } else {
        const date = new Date(label)
        return date.toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' })
      }
    })
    
    return new Chart(ctx, {
      type: 'bar',
      data: {
        labels: formattedLabels,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function(context) {
                const label = context.dataset.label || ''
                const value = context.parsed.y || 0
                return `${label}: $${value.toFixed(6)}`
              },
              footer: function(tooltipItems) {
                let sum = 0
                tooltipItems.forEach(item => {
                  sum += item.parsed.y
                })
                return `Összesen: $${sum.toFixed(6)}`
              }
            }
          }
        },
        scales: {
          x: {
            stacked: true,
            title: {
              display: true,
              text: this.currentPeriod === 'monthly' ? 'Hónap' : 'Hét'
            },
            ticks: {
              maxRotation: 45,
              minRotation: 45
            }
          },
          y: {
            stacked: true,
            beginAtZero: true,
            title: {
              display: true,
              text: 'Költség (USD)'
            },
            ticks: {
              callback: function(value) {
                return '$' + value.toFixed(4)
              }
            }
          }
        }
      }
    })
  }
}
